import { useCallback, useEffect, useRef } from 'react';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useUserConsent } from '@shared/utils/userConsent';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import type { Maybe } from '@graphql/generated-base';

/**
 * Returns a stable callback that checks whether a hotel should display the "Newly Added" badge.
 *
 * Detection mirrors v1 TagsOnThumbnail logic:
 * - User has seen the accommodations tour (session gate, resets on mount)
 * - Event has a venue
 * - Hotel was auto-suggested for the current venue
 * - Hotels are not from the first-time auto-population batch
 *
 * Call this hook once per list (not per tile) to avoid redundant work.
 * Pass the result to individual tiles as a lookup function.
 */
export const useIsNewlyAddedHotel = (isFirstTimeAutoPopulation: boolean) => {
  const { venueInfo } = useEventPlaces();
  const { getUserConsent } = useUserConsent();
  const queryParams = useQueryParams();

  // Resets on mount — matches v1 useRef behavior in TagsOnThumbnail
  const hasSeenNewlyAddedTagRef = useRef(false);

  const shouldForceShowTag = queryParams.showNewTag === 'true';
  const hasSeenAccommodationsTour = getUserConsent('hasSeenAccommodationsTour');

  // Mark the newly added tag as seen after user sees accommodations tour
  useEffect(() => {
    if (!hasSeenAccommodationsTour && !hasSeenNewlyAddedTagRef.current) {
      hasSeenNewlyAddedTagRef.current = true;
    }
  }, [hasSeenAccommodationsTour]);

  const canShowNewlyAdded = hasSeenNewlyAddedTagRef.current && venueInfo.hasVenue && (shouldForceShowTag || !isFirstTimeAutoPopulation);
  const venueGooglePlaceId = venueInfo.googlePlaceId;

  return useCallback(
    (hotelVenueGooglePlaceId: Maybe<string> | undefined): boolean => {
      return Boolean(canShowNewlyAdded && venueGooglePlaceId === hotelVenueGooglePlaceId);
    },
    [canShowNewlyAdded, venueGooglePlaceId],
  );
};
